import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FiPlus, FiTrash2, FiSave, FiX, FiSend } from 'react-icons/fi';
import { toast } from 'react-toastify';
import styles from './InvoiceCreate.module.css';

interface InvoiceForm {
  clientId: string;
  issueDate: string;
  dueDate: string;
  notes: string;
  terms: string;
}

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface Client {
  id: string;
  name: string;
  email: string;
}

// Mock clients data
const mockClients: Client[] = [
  { id: '1', name: 'Acme Corporation', email: 'contact@acme.com' },
  { id: '2', name: 'Tech Solutions Inc', email: 'info@techsolutions.com' },
  { id: '3', name: 'Global Industries', email: 'hello@globalindustries.com' },
  { id: '4', name: 'Innovation Labs', email: 'contact@innovationlabs.com' },
  { id: '5', name: 'HutsTech', email: 'segodi@hutstech.co.za' }
];

export function InvoiceCreate() {
  const navigate = useNavigate();
  const [items, setItems] = useState<InvoiceItem[]>([
    { id: '1', description: '', quantity: 1, unitPrice: 0, total: 0 }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);

  const { register, handleSubmit, formState: { errors, isValid }, watch } = useForm<InvoiceForm>({
    defaultValues: {
      issueDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      terms: 'Invoice Terms and Conditions\n\nPayment is due within 5 days of the invoice date; late payments may incur interest at 3% per week. The client must notify the provider of any discrepancies within 14 days of receipt; otherwise, the invoice is deemed correct. The provider reserves the right to suspend services for unpaid invoices until payment is settled.'
    },
    mode: 'onChange'
  });

  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.1; // 10% tax
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleSendInvoice = () => {
    setShowSendOptions(true);
  };

  const handleSendLink = async () => {
    setIsSending(true);
    setShowSendOptions(false);
    try {
      // Simulate API call to generate and send link
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Invoice link sent successfully!');
    } catch (error) {
      toast.error('Failed to send invoice link');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendCopy = async () => {
    setIsSending(true);
    setShowSendOptions(false);
    try {
      // Simulate API call to send copy
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Invoice copy sent successfully!');
    } catch (error) {
      toast.error('Failed to send invoice copy');
    } finally {
      setIsSending(false);
    }
  };

  const onSubmit = async (data: InvoiceForm) => {
    if (items.some(item => !item.description || item.total === 0)) {
      toast.error('Please fill in all item details');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const invoiceData = {
        ...data,
        items,
        subtotal: calculateSubtotal(),
        tax: calculateTax(),
        total: calculateTotal(),
        status: 'draft',
        invoiceNumber: `INV-${Date.now()}`
      };

      console.log('Invoice created:', invoiceData);
      toast.success('Invoice created successfully!');
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to create invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Create New Invoice</h1>
          <p className={styles.subtitle}>Fill in the details below to create a new invoice</p>
        </div>
        <div className={styles.headerActions}>
          <button 
            onClick={() => navigate('/invoices')} 
            className={styles.cancelButton}
          >
            <FiX size={16} />
            Cancel
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <div className={styles.formGrid}>
          {/* Client Selection */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Client Information</h3>
            <div className={styles.formGroup}>
              <label className={styles.label}>Client *</label>
              <select 
                {...register('clientId', { required: 'Please select a client' })}
                className={`${styles.select} ${errors.clientId ? styles.error : ''}`}
              >
                <option value="">Select a client</option>
                {mockClients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              {errors.clientId && (
                <span className={styles.errorMessage}>{errors.clientId.message}</span>
              )}
            </div>
          </div>

          {/* Invoice Details */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Invoice Details</h3>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Issue Date *</label>
                <input
                  type="date"
                  {...register('issueDate', { required: 'Issue date is required' })}
                  className={`${styles.input} ${errors.issueDate ? styles.error : ''}`}
                />
                {errors.issueDate && (
                  <span className={styles.errorMessage}>{errors.issueDate.message}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Due Date *</label>
                <input
                  type="date"
                  {...register('dueDate', { required: 'Due date is required' })}
                  className={`${styles.input} ${errors.dueDate ? styles.error : ''}`}
                />
                {errors.dueDate && (
                  <span className={styles.errorMessage}>{errors.dueDate.message}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Invoice Items</h3>
            <button 
              type="button" 
              onClick={addItem} 
              className={styles.addItemButton}
            >
              <FiPlus size={16} />
              Add Item
            </button>
          </div>

          <div className={styles.itemsContainer}>
            <div className={styles.itemsHeader}>
              <div className={styles.itemDescription}>Description</div>
              <div className={styles.itemQuantity}>Quantity</div>
              <div className={styles.itemPrice}>Unit Price</div>
              <div className={styles.itemTotal}>Total</div>
              <div className={styles.itemActions}></div>
            </div>

            {items.map((item, index) => (
              <div key={item.id} className={styles.itemRow}>
                <div className={styles.itemDescription}>
                  <input
                    type="text"
                    placeholder="Item description"
                    value={item.description}
                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                    className={styles.itemInput}
                  />
                </div>
                <div className={styles.itemQuantity}>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                    className={styles.itemInput}
                  />
                </div>
                <div className={styles.itemPrice}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(e) => updateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                    className={styles.itemInput}
                  />
                </div>
                <div className={styles.itemTotal}>
                  ${item.total.toFixed(2)}
                </div>
                <div className={styles.itemActions}>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className={styles.removeItemButton}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className={styles.totalsSection}>
          <div className={styles.totalsRow}>
            <span>Subtotal:</span>
            <span>${calculateSubtotal().toFixed(2)}</span>
          </div>
          <div className={styles.totalsRow}>
            <span>Tax (10%):</span>
            <span>${calculateTax().toFixed(2)}</span>
          </div>
          <div className={`${styles.totalsRow} ${styles.totalRow}`}>
            <span>Total:</span>
            <span>${calculateTotal().toFixed(2)}</span>
          </div>
        </div>

        {/* Additional Information */}
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>Additional Information</h3>
          <div className={styles.formGroup}>
            <label className={styles.label}>Notes</label>
            <textarea
              {...register('notes')}
              placeholder="Additional notes for the client..."
              className={styles.textarea}
              rows={3}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Terms & Conditions</label>
            <textarea
              {...register('terms')}
              placeholder="Payment terms and conditions..."
              className={styles.textarea}
              rows={3}
            />
          </div>
        </div>

        {/* Invoice Preview */}
        <div className={styles.previewSection}>
          <h3 className={styles.previewTitle}>Invoice Preview</h3>
          <div className={styles.previewContent}>
            <div className={styles.previewHeader}>
              <h4>Invoice Summary</h4>
              <p><strong>Issue Date:</strong> {watch('issueDate')}</p>
              <p><strong>Due Date:</strong> {watch('dueDate')}</p>
            </div>
            
            <div className={styles.previewItems}>
              <h5>Items:</h5>
              <div className={styles.previewItemList}>
                {items.map((item, index) => (
                  <div key={item.id} className={styles.previewItem}>
                    <span>{item.description || `Item ${index + 1}`}</span>
                    <span>{item.quantity} × R{item.unitPrice.toFixed(2)} = R{item.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className={styles.previewTotals}>
              <div className={styles.previewTotalRow}>
                <span>Subtotal:</span>
                <span>R{calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className={styles.previewTotalRow}>
                <span>Tax (10%):</span>
                <span>R{calculateTax().toFixed(2)}</span>
              </div>
              <div className={`${styles.previewTotalRow} ${styles.previewGrandTotal}`}>
                <span>Total:</span>
                <span>R{calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            {watch('notes') && (
              <div className={styles.previewNotes}>
                <h5>Notes:</h5>
                <p>{watch('notes')}</p>
              </div>
            )}

            {watch('terms') && (
              <div className={styles.previewTerms}>
                <h5>Terms & Conditions:</h5>
                <p>{watch('terms')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className={styles.formActions}>
          <button 
            type="button" 
            onClick={() => navigate('/invoices')} 
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={isSubmitting}
            className={`${styles.submitButton} ${isSubmitting ? styles.loading : ''}`}
          >
            <FiSave size={16} />
            {isSubmitting ? 'Creating...' : 'Create Invoice'}
          </button>
          <button 
            type="button" 
            onClick={handleSendInvoice}
            disabled={isSubmitting || !isValid}
            className={`${styles.sendButton} ${isSending ? styles.loading : ''}`}
          >
            <FiSend size={16} />
            {isSending ? 'Sending...' : 'Send Invoice'}
          </button>
        </div>
      </form>

      {/* Send Options Modal */}
      <AnimatePresence>
        {showSendOptions && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className={styles.modal}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Send Invoice</h3>
                <button
                  onClick={() => setShowSendOptions(false)}
                  className={styles.modalCloseButton}
                >
                  <FiX size={20} />
                </button>
              </div>
              
              <div className={styles.modalContent}>
                <p className={styles.modalDescription}>
                  Choose how you would like to send this invoice:
                </p>
                
                <div className={styles.sendOptions}>
                  <button
                    onClick={handleSendLink}
                    disabled={isSending}
                    className={`${styles.sendOptionButton} ${styles.sendLinkButton}`}
                  >
                    <FiSend size={16} />
                    <div className={styles.sendOptionContent}>
                      <span className={styles.sendOptionTitle}>Send Link</span>
                      <span className={styles.sendOptionDescription}>
                        Send a secure link to the client to view the invoice online
                      </span>
                    </div>
                  </button>
                  
                  <button
                    onClick={handleSendCopy}
                    disabled={isSending}
                    className={`${styles.sendOptionButton} ${styles.sendCopyButton}`}
                  >
                    <FiSend size={16} />
                    <div className={styles.sendOptionContent}>
                      <span className={styles.sendOptionTitle}>Send Copy</span>
                      <span className={styles.sendOptionDescription}>
                        Send a copy of the invoice directly to the client's email
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
